import { reportLog } from "../sentry";

/**
 * App-wide structured logger backed by Sentry Logs.
 *
 * Use this anywhere in the app to emit a queryable log line (filterable by any
 * attribute, correlated with errors/traces). It is safe to call from any context
 * — it never throws and is a no-op when Sentry/logs are disabled (e.g. on
 * localhost or cipstaging). Pass a short, stable `message` (treat it like an
 * event name) and a flat bag of primitive attributes you'll want to filter on.
 *
 *   import { logger } from "../util/logger";
 *
 *   logger.info("reminder_created", { reminderId, patientId });
 *   logger.warn("wifi_setup_retry", { attempt, ssid });
 *   logger.error("checkout_failed", { step, code });
 *
 * `route` and `facilityId` are attached automatically. Levels map to Sentry log
 * severities: trace < debug < info < warn < error < fatal.
 */
export const logger = {
  trace: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("trace", message, attributes),
  debug: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("debug", message, attributes),
  info: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("info", message, attributes),
  warn: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("warn", message, attributes),
  error: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("error", message, attributes),
  fatal: (message: string, attributes?: Record<string, unknown>) =>
    reportLog("fatal", message, attributes),
};

export type AppLogger = typeof logger;
